import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { getOrganizationsApi } from '@/lib/api/organizationsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import { getInitials } from '@shared';
import type { Organization } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowRight, Award, ChevronLeft, ChevronRight, MapPin, Search, SlidersHorizontal } from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────
const BANNER_COLORS = [
  'from-purple-400 to-indigo-500',
  'from-blue-400 to-indigo-500',
  'from-orange-400 to-red-500',
  'from-emerald-400 to-teal-500',
  'from-cyan-400 to-blue-500',
  'from-amber-400 to-orange-500',
  'from-indigo-400 to-purple-500',
];

const AVATAR_BG = [
  'bg-purple-500',
  'bg-blue-500',
  'bg-orange-500',
  'bg-emerald-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-indigo-500',
];

// ─── Org Card ─────────────────────────────────────────────────
function OrgCard({ org }: { org: Organization }) {
  const idx = org.id % BANNER_COLORS.length;
  const logoFile = org.files?.find(f => f.usage === 'ORG_LOGO') ?? null;
  const coverFile = org.files?.find(f => f.usage === 'ORG_COVER') ?? null;
  return (
    <div className='bg-white rounded-3xl border border-slate-200 overflow-hidden hover-lift transition-all duration-300 group'>
      {/* Banner */}
      {coverFile ? (
        <img src={getFileUrl(coverFile)} alt='' className='h-36 w-full object-cover object-top' />
      ) : (
        <div className={`h-36 bg-gradient-to-br ${BANNER_COLORS[idx]} relative`}>
          <div className='absolute inset-0 opacity-20'>
            <div className='absolute top-4 right-4 w-20 h-20 rounded-full bg-white/20' />
            <div className='absolute bottom-4 left-4 w-14 h-14 rounded-full bg-white/10' />
          </div>
        </div>
      )}

      {/* Content */}
      <div className='px-6 pb-6 z-20 relative'>
        {logoFile ? (
          <img
            src={getFileUrl(logoFile)}
            alt={org.name}
            className='w-16 h-16 rounded-2xl -mt-10 object-cover shadow-lg border-4 border-white'
          />
        ) : (
          <div
            className={`w-16 h-16 ${AVATAR_BG[idx]} rounded-2xl -mt-10 flex items-center justify-center text-white font-bold text-lg shadow-lg border-4 border-white`}
          >
            {getInitials(org.name)}
          </div>
        )}

        <h3 className='font-bold text-lg mt-3 text-slate-900 group-hover:text-purple-600 transition-colors'>
          <Link to={ROUTES.ORGANIZATION_PROFILE.replace(':id', String(org.id))}>{org.name}</Link>
        </h3>
        <div className='flex items-center gap-1.5 text-sm text-slate-500 mt-1'>
          <MapPin className='w-3.5 h-3.5' />
          {org.city}, {org.department}
        </div>

        {org.description && <p className='text-sm text-slate-500 mt-3 line-clamp-2'>{org.description}</p>}

        {/* Tags */}
        <div className='flex flex-wrap gap-2 mt-4'>
          <span className='px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-lg'>{org.region}</span>
        </div>

        <Link
          to={ROUTES.ORGANIZATION_PROFILE.replace(':id', String(org.id))}
          className='mt-5 w-full flex items-center justify-center gap-2 bg-linear-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-slate-800 transition-colors'
        >
          Voir le profil <ArrowRight className='w-4 h-4' />
        </Link>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────
const PAGE_SIZE = 12;

const VALID_SORT_BY = ['name', 'city', 'createdAt'] as const;
type SortBy = (typeof VALID_SORT_BY)[number];

function parseSortBy(value: string | null): SortBy {
  return VALID_SORT_BY.includes(value as SortBy) ? (value as SortBy) : 'name';
}

function parsePage(value: string | null): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

export default function SearchOrganizations() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePage(searchParams.get('page'));
  const search = searchParams.get('search') ?? '';
  const sortBy = parseSortBy(searchParams.get('sortBy'));

  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    ...(search && { search }),
    sortBy,
    sortOrder: 'asc' as const,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: queryKeys.organizations.list(queryParams),
    queryFn: () => getOrganizationsApi(queryParams),
    placeholderData: keepPreviousData,
  });

  const orgs = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value || undefined, page: undefined });
    }, 400);
  }

  function clearSearch() {
    setSearchInput('');
    updateParams({ search: undefined, page: undefined });
  }

  function getPageNumbers(): (number | '...')[] {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  }

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title='Rechercher une organisation'
        description='Découvrez les organisations de pétanque en France. Clubs, comités et ligues qui organisent des concours sur BoulesConnect.'
      />
      {/* Hero Section */}
      <section className='pt-40 pb-20 px-6 bg-gradient-to-br from-purple-50 via-white to-pink-50'>
        <div className='max-w-5xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-purple-50 border border-purple-100 px-4 py-2 rounded-full mb-8'>
            <Award className='w-4 h-4 text-purple-600' />
            <span className='text-xs font-bold text-purple-700 uppercase tracking-wider'>
              {isLoading ? '…' : `${total.toLocaleString('fr-FR')} organisateurs référencés`}
            </span>
          </div>

          <h1 className='text-5xl md:text-6xl font-extrabold text-slate-900 leading-[1.1] mb-8'>
            Découvrez les meilleurs
            <br />
            <span className='bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent'>
              organisateurs de concours
            </span>
          </h1>

          <p className='text-lg text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed'>
            Trouvez les organisateurs qui font vibrer la pétanque en France. Des événements de qualité et une
            organisation sans faille.
          </p>

          {/* Search bar */}
          <div className='max-w-2xl mx-auto'>
            <div className='flex items-center bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden'>
              <div className='flex items-center gap-3 flex-1 px-6 py-4'>
                <Search className='w-5 h-5 text-slate-400 shrink-0' />
                <input
                  type='text'
                  value={searchInput}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder='Rechercher un organisateur par nom, ville...'
                  className='w-full text-lg text-slate-700 placeholder:text-slate-400 outline-none bg-transparent'
                />
                {isFetching && !isLoading && <span className='text-xs text-slate-400'>…</span>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + Results */}
      <section className='px-6 py-16'>
        <div className='max-w-7xl mx-auto flex flex-col lg:flex-row gap-8'>
          {/* Sidebar Filters */}
          <aside className='lg:w-1/4 shrink-0'>
            <div className='bg-white rounded-3xl border border-slate-200 p-6 sticky top-28'>
              <div className='flex items-center gap-2 mb-6'>
                <SlidersHorizontal className='w-5 h-5 text-purple-600' />
                <h3 className='font-bold text-lg'>Filtres</h3>
              </div>

              <div className='mb-6'>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Trier par</label>
                <select
                  value={sortBy}
                  onChange={e => {
                    updateParams({ sortBy: e.target.value, page: undefined });
                  }}
                  className='w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700'
                >
                  <option value='name'>Nom (A-Z)</option>
                  <option value='city'>Ville</option>
                  <option value='createdAt'>Plus récent</option>
                </select>
              </div>

              {search && (
                <button
                  onClick={clearSearch}
                  className='w-full bg-slate-100 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-200 transition-colors text-sm'
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          </aside>

          {/* Results */}
          <div className='flex-1'>
            <div className='flex items-center justify-between mb-6'>
              <p className='text-slate-500'>
                <strong className='text-slate-900'>{isLoading ? '…' : total.toLocaleString('fr-FR')}</strong>{' '}
                organisateurs trouvés
              </p>
            </div>

            {isLoading ? (
              <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className='h-72 animate-pulse rounded-3xl bg-slate-200' />
                ))}
              </div>
            ) : orgs.length === 0 ? (
              <div className='rounded-3xl border border-dashed border-slate-300 bg-white p-16 text-center'>
                <Award className='w-12 h-12 text-slate-300 mx-auto mb-4' />
                <p className='text-slate-500 font-medium'>Aucun organisateur trouvé</p>
                {search && (
                  <button
                    onClick={clearSearch}
                    className='mt-4 text-purple-600 hover:text-purple-700 text-sm font-semibold'
                  >
                    Effacer la recherche
                  </button>
                )}
              </div>
            ) : (
              <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6'>
                {orgs.map(org => (
                  <OrgCard key={org.id} org={org} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='flex items-center justify-center gap-2 mt-12'>
                <button
                  onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                  disabled={page === 1}
                  className='w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:border-purple-300 hover:text-purple-600 transition-colors disabled:opacity-40'
                >
                  <ChevronLeft className='w-5 h-5' />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`d-${i}`} className='px-2 text-slate-400'>
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => updateParams({ page: String(p) })}
                      className={`w-10 h-10 flex items-center justify-center rounded-xl font-semibold text-sm transition-colors ${
                        p === page
                          ? 'bg-purple-600 text-white'
                          : 'border border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-600'
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                  disabled={page >= totalPages}
                  className='w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:border-purple-300 hover:text-purple-600 transition-colors disabled:opacity-40'
                >
                  <ChevronRight className='w-5 h-5' />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='px-6 py-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-linear-to-br from-purple-600 via-indigo-600 to-cyan-500 rounded-3xl p-12 text-center text-white relative overflow-hidden'>
            <div className='absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2' />
            <div className='absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2' />
            <div className='relative z-10'>
              <Award className='w-12 h-12 text-yellow-300 mx-auto mb-4' />
              <h2 className='text-3xl md:text-4xl font-extrabold mb-4'>Vous êtes organisateur de concours ?</h2>
              <p className='text-purple-100 text-lg mb-8 max-w-xl mx-auto'>
                Rejoignez BoulesConnect et bénéficiez d&apos;outils puissants pour gérer vos concours, attirer des
                participants et développer votre notoriété.
              </p>
              <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
                <Link
                  to={ROUTES.REGISTER}
                  className='bg-white text-purple-700 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-50 transition-colors hover-lift flex items-center gap-2'
                >
                  Créer mon compte <ArrowRight className='w-5 h-5' />
                </Link>
                <Link
                  to={ROUTES.TARIFS}
                  className='border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 transition-colors'
                >
                  Voir les tarifs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
